import React, { useEffect, useState, useCallback, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { commonPageFieldList, commonPageFieldListSuccess, GetVenderSupplierCustomer, GetVenderSupplierCustomerSuccess } from "../../../store/actions";
import * as pageId from "../../../routes/allPageID"
import * as url from "../../../routes/route_url";
import * as _cfunc from "../../../components/Common/CommonFunction";
import { PageLoadingSpinner, SaveButton } from "../../../components/Common/CommonButton";
import { MetaTags } from "react-meta-tags";
import { useHistory } from "react-router-dom";
import { mode } from "../../../routes";
import SaveButtonDraggable from "../../../components/Common/saveButtonDraggable";
import { FranchiseLicense_Get_Action, FranchiseLicense_Post_Action } from "../../../store/Administrator/FranchiseLiecense/action";
import GlobalCustomTable from "../../../GlobalCustomTable";
import { C_DatePicker } from "../../../CustomValidateForm";
import { customAlert } from "../../../CustomAlert/ConfirmDialog";


// ✅ Memoized DatePicker cell — only re-renders when ITS OWN value changes
const DatePickerCell = memo(({ rowKey, value, onDateChange }) => {
    return (
        <C_DatePicker
            value={value}
            onChange={(e, date) => onDateChange(rowKey, date)}
            options={{
                altInput: true,
                altFormat: "d-m-Y",
                dateFormat: "Y-m-d"
            }}
        />
    );
}, (prevProps, nextProps) => {
    return prevProps.value === nextProps.value && prevProps.rowKey === nextProps.rowKey;
});

DatePickerCell.displayName = "DatePickerCell";


const FranchiseLicense = (props) => {

    const dispatch = useDispatch();
    const history = useHistory();
    const currentDate_ymd = _cfunc.date_ymd_func();
    const [pageMode] = useState(mode.defaultsave);
    const [userPageAccessState, setUserAccState] = useState('');
    const [licenseRows, setLicenseRows] = useState([]);

    // ✅ Track which rows have been modified by the user
    const [modifiedKeys, setModifiedKeys] = useState(new Set());

    const hasShowModal = props.hasOwnProperty(mode.editValue);
    const location = { ...history.location };

    const reducers = useSelector((state) => ({
        tableList: state.FranchiseLicenseReducer.franchiseLicenseList,
        saveBtnLoading: state.FranchiseLicenseReducer.saveLoading,
        // ✅ Listen to updateMsg / success flag from reducer to re-fetch
        updateMsg: state.FranchiseLicenseReducer.updateMsg,
        userAccess: state.Login.RoleAccessUpdateData,
        pageField: state.CommonPageFieldReducer.pageFieldList,
    }));

    const { pageField, saveBtnLoading, userAccess, tableList, updateMsg } = reducers;

    useEffect(() => {
        dispatch(GetVenderSupplierCustomerSuccess([]));
        dispatch(GetVenderSupplierCustomer({
            subPageMode: url.FRANCHISE_LICENSE,
            PartyID: _cfunc.loginSelectedPartyID()
        }));
        return () => dispatch(GetVenderSupplierCustomerSuccess([]));
    }, []);

    useEffect(() => {
        let locationPath = location.pathname;
        if (hasShowModal) locationPath = props.masterPath;

        const userAcc = userAccess.find((inx) => `/${inx.ActualPagePath}` === locationPath);
        if (userAcc) {
            setUserAccState(userAcc);
            _cfunc.breadcrumbReturnFunc({ dispatch, userAcc });
        }
    }, [userAccess]);

    useEffect(() => {
        dispatch(commonPageFieldList(pageId.FRANCHISE_LICENSE));
        return () => dispatch(commonPageFieldListSuccess(null));
    }, []);

    useEffect(() => {
        dispatch(FranchiseLicense_Get_Action());
    }, []);

    // ✅ Bind latest tableList into rows
    useEffect(() => {
        if (tableList?.length) {

            setLicenseRows(
                tableList.map((item, index) => ({
                    ...item,
                    _rowKey: `${item.id}_${index}`,
                    NewVal: ""
                }))
            );
        }
    }, [tableList]);

    useEffect(() => {
        if (updateMsg?.Status === true) {

            customAlert({
                Type: 1,
                Message: updateMsg.Message
            });

            dispatch(FranchiseLicense_Get_Action());
            setModifiedKeys(new Set());
        }

    }, [updateMsg]);
    // ✅ On date change — update the row AND mark it as modified
    const handleDateChange = useCallback((rowKey, date) => {
        setLicenseRows(prev =>
            prev.map(item =>
                item._rowKey === rowKey
                    ? { ...item, NewVal: date }
                    : item
            )
        );
        // Mark this row as modified
        setModifiedKeys(prev => new Set(prev).add(rowKey));
    }, []);

    // ✅ Only send modified rows to the API
    const UpdateHandler = useCallback(() => {
        const rowsToUpdate = licenseRows.filter(item => modifiedKeys.has(item._rowKey));

        if (!rowsToUpdate.length) return; // nothing changed

        const jsonBody = JSON.stringify(
            rowsToUpdate.map(item => ({
                id: item.id,
                Validity: item.NewVal
            }))
        );

        dispatch(FranchiseLicense_Post_Action({ jsonBody }));
    }, [licenseRows, modifiedKeys, dispatch]);


    const tableColumns = [
        {
            dataField: "Name",
            text: "Franchise Name"
        },
        {
            dataField: "MacID",
            text: "Mac ID"
        },
        {
            dataField: "MachineName",
            text: "Machine Name",
            formatter: (cell) => cell || "-"
        },
        {
            dataField: "Validity",
            text: "Current Validity",
            formatter: (cell) => cell || "-"
        },
        {
            dataField: "NewVal",
            text: "New Validity",
            formatter: (cell, row) => (
                <DatePickerCell
                    rowKey={row._rowKey}
                    value={cell}
                    onDateChange={handleDateChange}
                />
            )
        }
    ];

    // ✅ Dynamic button label based on modified state
    const hasModifiedRows = modifiedKeys.size > 0;

    return (
        <React.Fragment>
            <MetaTags>{_cfunc.metaTagLabel(userPageAccessState)}</MetaTags>
            <PageLoadingSpinner />
            <div className="page-content">
                {pageField && (
                    <div className="mt-n1">
                        <GlobalCustomTable
                            keyField="_rowKey"
                            data={licenseRows}
                            columns={tableColumns}
                            search
                            bordered
                            striped
                            paginationEnabled
                            classes="table align-middle table-nowrap table-hover"
                            noDataIndication={
                                <div className="text-danger text-center">
                                    No Franchise Found
                                </div>
                            }
                        />
                    </div>
                )}
            </div>
            <SaveButtonDraggable>
                {/* ✅ Button label changes: idle → "Update (N)" → loading → "Updating..." */}
                <SaveButton
                    pageMode={pageMode}
                    onClick={UpdateHandler}
                    module={"MappingMaster"}
                    userAcc={userPageAccessState}
                    isLoading={saveBtnLoading}
                    // Pass a custom label if your SaveButton supports it,
                    // otherwise wrap with a disabled/label override like below:
                    label={
                        saveBtnLoading
                            ? "Updating..."
                            : hasModifiedRows
                                ? `Update (${modifiedKeys.size})`
                                : "Update"
                    }
                    disabled={!hasModifiedRows || saveBtnLoading}
                />
            </SaveButtonDraggable>
        </React.Fragment>
    );
};

export default FranchiseLicense;